import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useHistory} from 'react-router-dom';
import {ThemeProvider} from 'styled-components';
import _ from 'lodash';

import {labels} from '../../../util/utils';
import Utility from '../../../util/utilityfunctions';
import {UserType} from '../../../util/uiconstants';
import selectAccessPermissions from '../../../reducers/Project/selectors/selectAccessPermissions';
import {cleanProjectDetails, getProjectDetails} from '../../../actions/Project/projectAction';

import theme from '@ey/styled-theme';
import MotifNav from '@ey/canvascoreservices/MotifNav';

import StyledNav from './StyledNav';
import getUserSettingsLink from './links/getUserSettingsLink';
import getHelpLink from './links/getHelpLink';
import getContactListLink from './links/getContactListLink';
import getGroupRiskLink from './links/getGroupRiskLink';
import getDashboardLink from './links/getDashboardLink';

function Nav() {
	const location = useLocation();
	const history = useHistory();
	const dispatch = useDispatch();
	const projectId = new URLSearchParams(location && location.search).get('projectId');
	const project = useSelector((store) => store.project);
	const newProject = useSelector((store) => store.newProject);
	const isEditMode = useSelector((state) => state.isEditMode.activated);
	const userPreferences = useSelector((state) => state.userPreferences);
	const paansUserData = useSelector((state) => state.paansuserdata);
	const configuration = useSelector((state) => state.configurations);
	const {dashboardAccess, groupRiskAssessmentAccess, contactListAccess} = selectAccessPermissions();

	const getProjectData = (id) => dispatch(getProjectDetails(id));
	const cleanProjectData = () => dispatch(cleanProjectDetails());

	useEffect(() => {
		if (projectId) getProjectData(projectId);
		else cleanProjectData();
	}, [projectId]);

	const currentProjectId = projectId ? projectId : project && project.projectId;
	const isProjectAvailableAndPaansAccepted =
		projectId && !isEditMode && (paansUserData.isPaansEnabled === false || paansUserData.hasAccepted === true);
	const isProjectActive = !newProject.status === 'initial' || project.status === 'active';

	let engagementName = null;
	if (isEditMode) engagementName = newProject.name;
	else if (location && location.pathname && location.pathname.toLowerCase().includes('/admin'))
		engagementName = labels.administration;
	else if (projectId && newProject?.status !== 'initial') engagementName = project.name;

	const dashboardNavLink = {
		link: getDashboardLink(labels.dashboard, currentProjectId),
		condition: isProjectAvailableAndPaansAccepted && isProjectActive && dashboardAccess
	};

	const graNavLink = {
		link: getGroupRiskLink(labels.groupRiskAssessment, currentProjectId),
		condition:
			isProjectAvailableAndPaansAccepted &&
			isProjectActive &&
			groupRiskAssessmentAccess &&
			userPreferences.userType !== UserType.Guest
	};

	const contactsNavLink = {
		link: getContactListLink(labels.contactListLabel, currentProjectId),
		condition: isProjectAvailableAndPaansAccepted && isProjectActive && contactListAccess
	};

	const helpNavLink = {
		link: getHelpLink(labels.help, configuration?.features?.oversightHelpLink),
		condition: !_.isEmpty(configuration) && userPreferences.userType === UserType.Member
	};

	const navLinks = [],
		moduleLinks = [];

	if (dashboardNavLink.condition) moduleLinks.push(dashboardNavLink.link);
	if (graNavLink.condition) moduleLinks.push(graNavLink.link);
	if (contactsNavLink.condition) moduleLinks.push(contactsNavLink.link);
	if (moduleLinks.length > 0) navLinks.push(moduleLinks);
	navLinks.push(helpNavLink.link);

	const props = {
		showSelectorDropdown: false,
		showNotifications: false,
		showMoreMenuOptions: false,
		showSettings: false,
		showSearchBox: false,
		showIconLinks: true,
		applicationName: labels.canvasOversight,
		applicationClick: () => history.push('/'),
		applicationNameRedirectUrl: '/',
		teamsAvatar: getUserSettingsLink,
		topNavLinks: navLinks,
		offlinePrep: '',
		apponlinestate: true,
		connectivitystate: '',
		theme: Utility.getMotifTheme(),
		getBookmarkAction: () => {}
	};

	if (engagementName) props.applicationName = `${labels.canvasOversight} | ${engagementName}`;

	return (
		<ThemeProvider theme={theme}>
			<StyledNav>
				<MotifNav {...props} />
			</StyledNav>
		</ThemeProvider>
	);
}

export default Nav;
